import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  TextField,
  Button,
} from "@mui/material";
import GenericPopup from "@Components/Popup/Popup";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import useSendPutRequest from "@Hooks/useSendPutRequest";
import usePostItem from "@Hooks/usePostItem";
import { styled } from "@mui/system";
import { CONDITIONS_URL } from "../../API/APIRoutes";
import themeCreator from "@Themes/themeCreator";
import useGetItem from "@Hooks/useGetItem";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  "& .MuiTableCell-head": {
    backgroundColor: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    color: theme.palette.primary.contrastText,
    fontWeight: "bold",
    fontSize: "16px",
  },
  "& .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: theme.palette.datagrid.odd,
  },
  "& .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: theme.palette.datagrid.even,
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  color: "white",
  fontWeight: "bold",
  fontSize: "1rem",
  textAlign: "center",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: "center",
  fontSize: "0.95rem",
}));

const StyledAddButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.common.white,
  width: "24px",
  height: "24px",
  padding: "2px",
  boxShadow: "0 2px 8px rgba(15, 66, 170, 0.25)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    transform: "scale(1.1)",
    boxShadow: "0 4px 12px rgba(15, 66, 170, 0.4)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

export default function ConditionTable({ orderId }) {
  const [conditions, setConditions] = useState([]);
  const [conditionValues, setConditionValues] = useState({});
  const [errors, setErrors] = useState({});
  const { loading: PostItemLoading, postItem } = usePostItem();
  const getItem = useGetItem();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const theme = themeCreator();

  const inputRefs = useRef({});

  const conditionsTable = [
    { code: "ZCD0", name: "Custom Discharge", currency: "EGP" },
    { code: "ZC17", name: "Clearance Charges", currency: "EGP" },
    { code: "ZC17_DUP", name: "Clearance Charges", currency: "EGP" },
    { code: "ZDS0", name: "Discharge Shipping Line", currency: "EGP" },
    { code: "ZDS0_DUP", name: "Discharge Shipping Line", currency: "EGP" },
    { code: "ZSH0", name: "Storage & Handling", currency: "EGP" },
    { code: "ZSH0_DUP", name: "Storage & Handling", currency: "EGP" },
    { code: "ZT00", name: "Transportation", currency: "EGP" },
    { code: "ZT00_DUP", name: "Transportation", currency: "EGP" },
    { code: "ZXX5", name: "Official Receipts", currency: "EGP" },
    { code: "ZXX5_DUP", name: "Official Receipts", currency: "EGP" },
    { code: "ZXX1", name: "Other Health Approve", currency: "EGP" },
    { code: "ZXX4", name: "Stamps", currency: "EGP" },
    { code: "ZXX3", name: "Other Clearance", currency: "EGP" },
    { code: "ZXX3_DUP", name: "Other Clearance", currency: "EGP" },
    { code: "ZSF0_ZSA0", name: "Sea / Air Freight (Value)", currency: "EGP" },
    { code: "ZIS1", name: "Import Sales Tax (%)", currency: "%" },
    { code: "ZOM1", name: "Customs (%)", currency: "%" },
  ];

  useEffect(() => {
    setIsPageLoading(true);
    getItem(CONDITIONS_URL, orderId)
      .then((initialConditions) => {
        const processed = conditionsTable.map((condition) => {
          const found = initialConditions.find(
            (c) => c.conditionCode === condition.code
          );
          return {
            ...condition,
            value: found?.value || 0,
            edit: !!found?.value,
          };
        });
        setConditions(processed);

        const values = {};
        processed.forEach((c) => {
          values[c.code] = c.value;
        });
        setConditionValues(values);

        setIsPageLoading(false);
      })
      .catch(() => setIsPageLoading(false));
  }, [orderId]);

  const handleTextFieldChange = (event, code) => {
    const { value } = event.target;
    setConditionValues((prev) => ({
      ...prev,
      [code]: value,
    }));
  };

  const handlePaste = (event, startCode) => {
    const pasteText = event.clipboardData.getData("text");
    const values = pasteText.split(/\r?\n/).filter((v) => v.trim() !== "");
    if (values.length === 0) return;

    event.preventDefault();

    const startIndex = conditions.findIndex((c) => c.code === startCode);
    if (startIndex === -1) return;

    setConditionValues((prev) => {
      const updated = { ...prev };
      values.forEach((val, offset) => {
        const target = conditions[startIndex + offset];
        if (target) {
          updated[target.code] = val.trim();
        }
      });
      return updated;
    });

    // focus the last filled field
    const lastTarget = conditions[startIndex + values.length - 1];
    if (lastTarget) {
      setTimeout(() => {
        inputRefs.current[lastTarget.code]?.focus();
      }, 0);
    }
  };

  const handleSave = () => {
    const newErrors = {};
    // Create a new object to hold updated values
    const updatedConditionValues = { ...conditionValues };
    conditions.forEach((condition) => {
      let val = updatedConditionValues[condition.code];
      if (val === "" || val === undefined || val === null) {
        val = 0;
        updatedConditionValues[condition.code] = 0;
      }
      if (isNaN(val)) {
        newErrors[condition.code] = "Invalid number";
      } else if (parseFloat(val) < 0) {
        newErrors[condition.code] = "Value cannot be negative";
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setConditionValues(updatedConditionValues); // update UI with 0s
      return;
    }
    setErrors({});
    setConditionValues(updatedConditionValues); // update UI with 0s
    // Prepare payload
    const condtions = Object.entries(updatedConditionValues).map(
      ([code, val]) => ({
        conditionCode: code,
        value: parseFloat(val),
      })
    );
    postItem(
      CONDITIONS_URL + `/${orderId}`,
      condtions,
      () => {},
      "Condition values added successfully",
      "Failed to add condition values"
    );
  };

  if (isPageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <StyledTableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>Code</StyledHeaderCell>
              <StyledHeaderCell>Name</StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "15vw" }}>Value</StyledHeaderCell>
              <StyledHeaderCell>Currency</StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.map((condition, index) => (
              <TableRow
                key={condition.code}
                hover
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: "pointer",
                }}
              >
                <StyledTableCell>
                  {condition.code === "ZSF0_ZSA0"
                    ? "ZSF0 / ZSA0"
                    : condition.code}
                </StyledTableCell>

                <StyledTableCell>{condition.name}</StyledTableCell>

                <StyledTableCell>
                  <Box noValidate autoComplete="off">
                    <Grid item xs={12}>
                      <TextField
                        key={condition.code}
                        inputRef={(el) =>
                          (inputRefs.current[condition.code] = el)
                        }
                        variant="outlined"
                        fullWidth
                        value={conditionValues[condition.code] ?? ""}
                        onChange={(e) =>
                          handleTextFieldChange(e, condition.code)
                        }
                        onPaste={(e) => handlePaste(e, condition.code)}
                        error={!!errors[condition.code]}
                        helperText={errors[condition.code] || ""}
                        placeholder="Enter value"
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const next = conditions[index + 1];
                            if (next) {
                              inputRefs.current[next.code]?.focus();
                            }
                          }
                        }}
                      />
                    </Grid>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>{condition.currency}</StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{
            height: "5vh",
            minWidth: "10vw",
            mb: 4,
            mr: 1,
            fontWeight: "bold",
          }}
        >
          {PostItemLoading ? (
            <LoadingComponent size="2em" thickness={5} color="white" />
          ) : (
            "Save Condition Table"
          )}
        </Button>
      </Box>
    </Box>
  );
}
